/**
 * Onboarding module: the one-time welcome wizard (skippable, resumable) and the guided page tours.
 * Progress is stored per user in `user_onboarding` (owner-private, FORCE RLS). Shapes and routes only.
 * Registered by `export * from './mod-onboarding.js';` in index.ts.
 */
import { z } from 'zod';
import { E164, Id, Timestamp } from './common.js';

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'use HH:MM (24 hour, IST)');

/**
 * One tour per sidebar page (key = the page's nav key), one per Business setup tab (`setup-<tab>`), the person
 * side panel (`person`) and the short walk shown after the welcome (`welcome-next`). Keys are only ever added:
 * a stored `tours_seen` entry must stay valid.
 */
export const PAGE_TOUR_KEYS = ['today', 'calendar', 'pipeline', 'capture', 'draft', 'reach', 'media', 'strategy', 'products', 'laws', 'reports', 'review', 'setup', 'tools', 'users', 'settings'] as const;
export const SETUP_TAB_TOUR_KEYS = ['setup-profile', 'setup-knowledge', 'setup-rules', 'setup-ai', 'setup-lists', 'setup-timing', 'setup-transfer', 'setup-models'] as const;
export const TOUR_KEYS = [...PAGE_TOUR_KEYS, ...SETUP_TAB_TOUR_KEYS, 'person', 'welcome-next'] as const;
export const TourKey = z.enum(TOUR_KEYS);
export type TourKey = z.infer<typeof TourKey>;

export const TourStatus = z.enum(['done', 'skipped']);
export const TourSeen = z.object({ status: TourStatus, at: Timestamp });
export const ToursSeen = z.record(TourKey, TourSeen);
export type ToursSeen = z.infer<typeof ToursSeen>;

// ---------------------------------------------------------------- wizard steps
/** Step 1 "Hi <name>": your name, your own WhatsApp number (reminders to yourself only), morning brief time. */
export const HelloStep = z.object({
  name: z.string().trim().min(1, 'please type your name').max(120),
  whatsapp: z.union([z.literal(''), E164]),
  briefTime: HHMM,
}).strict();
/** Step 2 "Your businesses": the products/services you personally sell, per area you belong to. */
export const BusinessesStep = z.object({
  areas: z.array(z.object({ verticalId: Id, productIds: z.array(Id).max(200) }).strict()).max(20),
}).strict();
/** Step 3 "Add your first clients": how many were added (the people themselves go through the normal contact routes). */
export const ClientsStep = z.object({
  added: z.number().int().min(0).max(5000),
  skipped: z.boolean(),
}).strict();
/** Step 4 "How your day works": the user has read the three cards. */
export const DayStep = z.object({ seen: z.literal(true) }).strict();

export const OnboardingSteps = z.object({
  hello: HelloStep.optional(),
  businesses: BusinessesStep.optional(),
  clients: ClientsStep.optional(),
  day: DayStep.optional(),
}).strict();
export type OnboardingSteps = z.infer<typeof OnboardingSteps>;

export const SaveStepRequest = z.discriminatedUnion('step', [
  z.object({ step: z.literal('hello'), data: HelloStep }).strict(),
  z.object({ step: z.literal('businesses'), data: BusinessesStep }).strict(),
  z.object({ step: z.literal('clients'), data: ClientsStep }).strict(),
  z.object({ step: z.literal('day'), data: DayStep }).strict(),
]);
export type SaveStepRequest = z.infer<typeof SaveStepRequest>;

export const FinishOnboardingRequest = z.object({ skipped: z.boolean() }).strict();
export const MarkTourRequest = z.object({ status: TourStatus }).strict();

// ---------------------------------------------------------------- state
export const OnboardingProduct = z.object({ id: Id, name: z.string(), tier: z.string().nullable() });
export const OnboardingArea = z.object({
  verticalId: Id,
  key: z.string(),
  name: z.string(),
  /** One line from the area profile ("what we sell"), or '' when the area has none yet. */
  description: z.string(),
  products: z.array(OnboardingProduct),
  selectedProductIds: z.array(Id),
});
export type OnboardingArea = z.infer<typeof OnboardingArea>;

export const OnboardingState = z.object({
  /** true when the wizard should open by itself (never finished or skipped). */
  showWizard: z.boolean(),
  currentStep: z.number().int().min(1).max(5),
  completedAt: Timestamp.nullable(),
  skipped: z.boolean(),
  steps: OnboardingSteps,
  toursSeen: ToursSeen,
  /** Prefilled answers for step 1 (saved values, else the account's name/phone and the area's brief time). */
  hello: z.object({ name: z.string(), whatsapp: z.string(), briefTime: HHMM }),
  areas: z.array(OnboardingArea),
});
export type OnboardingState = z.infer<typeof OnboardingState>;

// ---------------------------------------------------------------- route table (prefix /api/v1)
type Def = { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; path: string; query?: z.ZodTypeAny; body?: z.ZodTypeAny; response: z.ZodTypeAny };
const d = <T extends Def>(x: T): T => x;

export const onboardingRoutes = {
  getOnboarding: d({ method: 'GET', path: '/onboarding', response: OnboardingState }),
  saveOnboardingStep: d({ method: 'PUT', path: '/onboarding/steps', body: SaveStepRequest, response: OnboardingState }),
  finishOnboarding: d({ method: 'POST', path: '/onboarding/finish', body: FinishOnboardingRequest, response: OnboardingState }),
  restartOnboarding: d({ method: 'POST', path: '/onboarding/restart', response: OnboardingState }),
  markTour: d({ method: 'PUT', path: '/onboarding/tours/:tourKey', body: MarkTourRequest, response: OnboardingState }),
  resetTours: d({ method: 'DELETE', path: '/onboarding/tours', response: OnboardingState }),
} as const;
export type OnboardingRouteName = keyof typeof onboardingRoutes;
